import "../../components/welcomeScreen/welcomeWizard.scss";
import "./syncSettings.scss";

import { filter } from "lodash";
import PropTypes from "prop-types";
import React, { Fragment, PureComponent } from "react";

import { withContainerContext } from "../../components/container/ContainerContext";
import FormButton from "../../components/form/FormButton";
import FormDropdown from "../../components/form/FormDropdown";
import FormDropdownMenuTrigger from "../../components/form/FormDropdownMenuTrigger";
import FormListButtonWrapper from "../../components/form/FormListButtonWrapper";
import Icon from "../../components/interface/Icon";
import ModalBlock from "../../components/modal/ModalBlock";
import ModalField from "../../components/modal/ModalField";
import ModalGroup from "../../components/modal/ModalGroup";
import ModalRow from "../../components/modal/ModalRow";
import syncApplications from "../../constants/syncApplications";
import { typeSelectors } from "../../modules/definition/types";
import { syncSelectors } from "../../modules/sync";
import { syncSettingsOperations, syncSettingsSelectors } from "../../modules/sync/settings";
import { immutableRecords, PropTypeShapes } from "../../types";
import { getLabelWithUntitled } from "../../utils/labelUtils";
// import FormButton from "../../components/form/FormButton";
// import TreeView from "../../components/treeView";
import selectors from "./selectors";

// const ULYSSES_SETTINGS_MANUSCRIPT_ROOT_VIEW_ID = "ULYSSES_SETTINGS_MANUSCRIPT_ROOT_VIEW_ID";

const folderFlex = [1, 4];
const fieldFlex = [3, 5, 5];

const flatOptions = [
  {
    value: 1,
    label: "Flat list",
    description:
      "All items of the selected type are added directly underneath the chosen folder. Items underneath this level in the folder are ignored during syncing."
  },
  {
    value: 0,
    label: "Nested Items",
    description:
      "Items will be nested underneath parent items. Syncing will occur at multiple levels, as defined by further settings."
  }
];

const getTypeOptions = types =>
  types
    .map(t => {
      return { value: t.id, label: t.label };
    })
    .unshift({ value: null, label: "None" })
    .toArray();

const levelHeaders = [
  { label: "Level", flex: fieldFlex[0] },
  {
    label: "Document",
    flex: fieldFlex[1],
    help: "Select the item type to sync Documents to at each nested level"
  },
  {
    label: "Folder",
    flex: fieldFlex[2],
    help: "Select the item type to sync Folders to at each nested level"
  }
];

const getFolderHeaders = appName => {
  return [
    { label: `${appName} Folder`, flex: folderFlex[0] },
    { label: "Timeline Item Type", flex: folderFlex[1] }
  ];
};

const renderLevelRow = (
  key,
  levelLabel,
  superOptions,
  documentType,
  folderType,
  onChangeDocumentType,
  onChangeFolderType,
  onRemoveLevel
) => {
  return (
    <ModalRow key={key} border={true} alignMiddle={true}>
      <ModalField flex={fieldFlex[0]}>
        <span>{levelLabel}</span>
      </ModalField>
      <ModalField flex={fieldFlex[1]}>
        <div className="entityFolderDropdownAndDelete">
          <FormDropdown options={superOptions} value={documentType} onChange={onChangeDocumentType} />
          <Icon icon="blank" color="iconMono" />
        </div>
      </ModalField>
      <ModalField flex={fieldFlex[2]}>
        <div className="entityFolderDropdownAndDelete">
          <FormDropdown options={superOptions} value={folderType} onChange={onChangeFolderType} />
          <Icon icon={onRemoveLevel ? "listDelete" : "blank"} color="iconMono" onClick={onRemoveLevel} />
        </div>
      </ModalField>
    </ModalRow>
  );
};

/**
 *
 * @param {string} key
 * @param {array<object>} labelData
 * @returns Component
 */
const renderHeaderRow = (key, labelData) => {
  return (
    <ModalRow key={key} border={true} alignMiddle={true}>
      {labelData.map(l => {
        return <ModalField key={l.label} flex={l.flex} helpTooltip={l.help} label={l.label} />;
      })}
    </ModalRow>
  );
};

const renderAddEntityFolder = (folderOptions, onAddEntityFolder) => {
  return (
    <ModalGroup key="addEntityFolder">
      <FormDropdownMenuTrigger
        targetId="addEntityFolder"
        options={folderOptions}
        onOptionClick={onAddEntityFolder}
        allowParentSelection={true}
      >
        <FormListButtonWrapper>
          <div id="addEntityFolder" className="tertiaryBtn" style={{ marginTop: "10px" }}>
            <svg className={"icon"}>
              <use xlinkHref="#listAdd" />
            </svg>
            <span>Add Folder</span>
          </div>
        </FormListButtonWrapper>
      </FormDropdownMenuTrigger>
    </ModalGroup>
  );
};

class SyncFolderSelectionPage extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {};

    this.onNarrativeFolderChange = this.onNarrativeFolderChange.bind(this);
    this.onAddEntityFolder = this.onAddEntityFolder.bind(this);
    this.onRemoveEntityFolder = this.onRemoveEntityFolder.bind(this);
    this.onChangeMainTypeForEntityFolder = this.onChangeMainTypeForEntityFolder.bind(this);
    this.onChangeFlattenedForEntityFolder = this.onChangeFlattenedForEntityFolder.bind(this);
    this.onAddLevelForEntityFolder = this.onAddLevelForEntityFolder.bind(this);
    this.onRemoveLevelForEntityFolder = this.onRemoveLevelForEntityFolder.bind(this);
    this.onChangeDefaultDocumentTypeForEntityFolder = this.onChangeDefaultDocumentTypeForEntityFolder.bind(this);
    this.onChangeDocumentTypeForLevelInEntityFolder = this.onChangeDocumentTypeForLevelInEntityFolder.bind(this);
    this.onChangeDocumentTypeForLevelInEntityFolder = this.onChangeDocumentTypeForLevelInEntityFolder.bind(this);
    this.onChangeFolderTypeForLevelInEntityFolder = this.onChangeFolderTypeForLevelInEntityFolder.bind(this);
  }

  onNarrativeFolderChange(value) {
    this.props.dispatchLocal(syncSettingsOperations.setNarrativeFolderId(value));
  }

  onAddEntityFolder(value) {
    this.props.dispatchLocal(syncSettingsOperations.addEntityFolder(value));
  }

  onRemoveEntityFolder(value) {
    this.props.dispatchLocal(syncSettingsOperations.removeEntityFolder(value));
  }

  onChangeMainTypeForEntityFolder(folderId, typeId) {
    this.props.dispatchLocal(syncSettingsOperations.setMainTypeForEntityFolder(folderId, typeId));
  }

  onChangeFlattenedForEntityFolder(folderId, flattened) {
    this.props.dispatchLocal(syncSettingsOperations.setFlattenedForEntityFolder(folderId, flattened));
  }

  onAddLevelForEntityFolder(folderId) {
    this.props.dispatchLocal(syncSettingsOperations.addLevelOfEntityFolder(folderId));
  }

  onRemoveLevelForEntityFolder(folderId, index) {
    this.props.dispatchLocal(syncSettingsOperations.removeLevelOfEntityFolder(folderId, index));
  }

  onChangeDefaultDocumentTypeForEntityFolder(folderId, typeId) {
    this.props.dispatchLocal(syncSettingsOperations.setDefaultTypeForDocumentInEntityFolder(folderId, typeId));
  }

  onChangeDefaultFolderTypeForEntityFolder(folderId, typeId) {
    this.props.dispatchLocal(syncSettingsOperations.setDefaultTypeForFolderInEntityFolder(folderId, typeId));
  }

  onChangeDocumentTypeForLevelInEntityFolder(folderId, level, typeId) {
    this.props.dispatchLocal(syncSettingsOperations.setTypeForDocumentAtLevelOfEntityFolder(folderId, level, typeId));
  }

  onChangeFolderTypeForLevelInEntityFolder(folderId, level, typeId) {
    this.props.dispatchLocal(syncSettingsOperations.setTypeForFolderAtLevelOfEntityFolder(folderId, level, typeId));
  }

  renderLoadingMessage() {
    const msg = this.props.ulyssesWaitingForAuth
      ? "Please authorize Aeon Timeline access within Ulysses application"
      : "Connecting to Ulysses...";
    return (
      <ModalRow>
        <div>{msg}</div>
      </ModalRow>
    );
  }

  renderNarrativeFolderGroup(folderOptions) {
    const narrativeFolder = syncSelectors.getSyncNarrativeFolderId(this.props.localStore);
    return (
      <ModalGroup key="narrativeFolder" label="Narrative Folder">
        <ModalRow>
          <ModalField>
            <FormDropdown
              options={folderOptions}
              onChange={this.onNarrativeFolderChange}
              value={narrativeFolder}
              allowParentSelection={true}
            />
          </ModalField>
        </ModalRow>
      </ModalGroup>
    );
  }

  renderEntityFolderLevel(entityFolder, levelIndex, superOptions) {
    const folderId = entityFolder.folderId;
    const level = entityFolder.levels.get(levelIndex);
    const key = `${folderId}-level-${levelIndex}`;
    const levelLabel = levelIndex === 0 ? "Top Level" : `Level ${levelIndex + 1}`;
    const onChangeDocumentType = value => this.onChangeDocumentTypeForLevelInEntityFolder(folderId, levelIndex, value);
    const onChangeFolderType = value => this.onChangeFolderTypeForLevelInEntityFolder(folderId, levelIndex, value);
    const onRemoveLevel = () => this.onRemoveLevelForEntityFolder(folderId, levelIndex);

    return renderLevelRow(
      key,
      levelLabel,
      superOptions,
      level.documentType,
      level.folderType,
      onChangeDocumentType,
      onChangeFolderType,
      onRemoveLevel
    );
  }

  renderDefaultEntityFolderLevel(entityFolder, superOptions) {
    const folderId = entityFolder.folderId;
    const key = `${folderId}-defaultLevel`;
    const levelLabel = entityFolder.levels.size ? "All below" : "All levels";
    const onChangeDocumentType = value => this.onChangeDefaultDocumentTypeForEntityFolder(folderId, value);
    const onChangeFolderType = value => this.onChangeDefaultFolderTypeForEntityFolder(folderId, value);
    const onRemoveLevel = null;
    return renderLevelRow(
      key,
      levelLabel,
      superOptions,
      entityFolder.defaultDocumentType,
      entityFolder.defaultFolderType,
      onChangeDocumentType,
      onChangeFolderType,
      onRemoveLevel
    );
  }

  renderEntityFolderDetails(entityFolder, typeOptions) {
    const folderId = entityFolder.folderId;
    const superTypeIds = typeSelectors.getAllSuperTypes(this.props.localStore, entityFolder.mainType);

    let superOptions = filter(typeOptions, o => o.value === entityFolder.mainType || superTypeIds.includes(o.value));
    superOptions.push({ value: null, label: "Ignore" });

    return (
      <ModalGroup className="entityFolderDetails" key={`${folderId}-details`} alignMiddle={true}>
        <ModalRow>
          <ModalField
            label="Structure"
            flex={fieldFlex[0]}
            helpTooltip="Specify the internal structure you wish to use inside this folder in your writing project"
          />
          <ModalField flex={fieldFlex[1] + fieldFlex[2]}>
            <FormDropdown
              options={flatOptions}
              value={entityFolder.flattened ? 1 : 0}
              showDescription={true}
              onChange={value => this.onChangeFlattenedForEntityFolder(folderId, Boolean(value))}
            />
          </ModalField>
        </ModalRow>
        {!entityFolder.flattened ? (
          <Fragment>
            {renderHeaderRow("entityFolderLevelHeader", levelHeaders)}
            {entityFolder.levels
              .map((_level, index) => this.renderEntityFolderLevel(entityFolder, index, superOptions))
              .toArray()}
            {this.renderDefaultEntityFolderLevel(entityFolder, superOptions)}
            <ModalRow key="entityFolderAddLevel" alignMiddle={true}>
              <ModalField flex={fieldFlex[0]}>
                <FormButton
                  linkStyle={true}
                  label="Insert level"
                  onClick={() => this.onAddLevelForEntityFolder(folderId)}
                />
              </ModalField>
              <ModalField flex={fieldFlex[1] + fieldFlex[2]} />
            </ModalRow>
          </Fragment>
        ) : null}
      </ModalGroup>
    );
  }

  renderEntityFolder(entityFolder, typeOptions) {
    const externalRep = this.getExternalRep();
    const folderId = entityFolder.folderId;

    const doc = externalRep.documents.get(folderId);
    const label = doc ? getLabelWithUntitled(doc.label) : "Untitled";

    let detailsRow = null;
    if (entityFolder.mainType) {
      detailsRow = (
        <ModalRow key={`${folderId}-details`} border={true} alignMiddle={true}>
          <ModalField flex={1}>
            <span>&nbsp;</span>
          </ModalField>
          <ModalField flex={4}>{this.renderEntityFolderDetails(entityFolder, typeOptions)}</ModalField>
        </ModalRow>
      );
    }

    return (
      <Fragment key={folderId}>
        <ModalRow key={folderId} border={false} alignMiddle={true}>
          <ModalField flex={1}>
            <span>{label}</span>
          </ModalField>
          <ModalField flex={4}>
            <div className="entityFolderDropdownAndDelete">
              <FormDropdown
                options={typeOptions}
                value={entityFolder.mainType}
                onChange={value => this.onChangeMainTypeForEntityFolder(folderId, value)}
              />
              <Icon icon="listDelete" color="iconMono" onClick={() => this.onRemoveEntityFolder(folderId)} />
            </div>
          </ModalField>
        </ModalRow>
        {detailsRow}
      </Fragment>
    );
  }

  renderEntityFoldersGroup(folderOptions) {
    const appName = syncSettingsSelectors.getSyncApplicationName(this.props.localStore);
    const entityFolders = syncSettingsSelectors.getSyncEntityFolders(this.props.localStore);

    const allowedTypes = typeSelectors.getSyncToEntityTypes(this.props.localStore);

    const typeOptions = getTypeOptions(allowedTypes);

    const folderHeaders = getFolderHeaders(appName);

    return (
      <ModalGroup key="entityFolders" label="Entity Folders">
        <ModalBlock>
          {renderHeaderRow("entityFolderHeaders", folderHeaders)}
          {entityFolders
            .toList()
            .map(f => this.renderEntityFolder(f, typeOptions))
            .toArray()}
          {renderAddEntityFolder(folderOptions, this.onAddEntityFolder)}
        </ModalBlock>
      </ModalGroup>
    );
  }

  getExternalRep() {
    const isUlyssesApp = syncSettingsSelectors.getSyncApplication(this.props.localStore) === syncApplications.ULYSSES;
    return isUlyssesApp ? this.props.ulyssesTree : this.props.localStore.sync.externalRep;
  }

  render() {
    const isUlyssesApp = syncSettingsSelectors.getSyncApplication(this.props.localStore) === syncApplications.ULYSSES;

    // If the user has selected Ulysses and we don't have access to the tree yet, then render
    // a message telling them we are waiting.
    if (isUlyssesApp && !this.props.ulyssesTree) {
      return <ModalGroup key="content">{this.renderLoadingMessage()}</ModalGroup>;
    }

    // Otherwise, we are able to present the interface properly
    const folderOnly = true;
    const externalRep = this.getExternalRep();
    const folderOptions = selectors.getFolderDropdownOptionsForRep(externalRep, folderOnly);

    return (
      <Fragment>
        {this.renderNarrativeFolderGroup(folderOptions)}
        {this.renderEntityFoldersGroup(folderOptions)}
      </Fragment>
    );
  }
}

SyncFolderSelectionPage.propTypes = {
  localStore: PropTypes.object.isRequired,
  dispatchLocal: PropTypes.func.isRequired,
  ulyssesTree: PropTypes.instanceOf(immutableRecords.SyncRepresentationRecord),
  ulyssesWaitingForAuth: PropTypes.bool,
  onBackClick: PropTypes.func.isRequired,
  onNextClick: PropTypes.func.isRequired,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired
};

export default withContainerContext(SyncFolderSelectionPage);
